<template>
  <div>
    <!-- <pre v-if="true" class="p-2 mb-2 text-xs bg-gray-100">
      Component: {{ widget.component }}
      Is Active: {{ isActive }}
      Resolved: {{ !!resolvedComponent }}
      Clean Name: {{ getCleanComponentName(widget.component) }}
    </pre> -->

    <component v-if="resolvedComponent && isActive" :is="resolvedComponent" v-bind="componentProps" />
  </div>
</template>

<script setup>
import { ref, computed, defineAsyncComponent, onMounted, inject } from "vue";
import { usePage } from "@inertiajs/vue3";
import JurusanStats from "../JurusanStats.vue";
import LayananStats from "../LayananStats.vue";
import HeroSection from "../HeroSection.vue";
import MedsosLink from "../MedsosLink.vue";

const props = defineProps({
  widget: {
    type: Object,
    required: true,
  },
});

// Fungsi untuk mendapatkan nama komponen murni - define outside of computed for reuse
const getCleanComponentName = (path) => {
  if (!path) return "";

  let name = path;

  // Jika ada slash, ambil bagian terakhir saja
  if (name.includes("/")) {
    name = name.split("/").pop();
  }

  // Hapus prefiks 'Web/' jika ada
  if (name.startsWith("Web/")) {
    name = name.substring(4);
  }

  // Hapus ekstensi .vue jika ada
  if (name.endsWith(".vue")) {
    name = name.slice(0, -4);
  }

  return name;
};

// Resolve component path dynamically
const resolvedComponent = ref(null);
const isActive = computed(() => {
  console.log("Widget active status:", props.widget.is_active);
  // Be more lenient with boolean type conversion
  return (
    props.widget.is_active === true ||
    props.widget.is_active === 1 ||
    props.widget.is_active === "1" ||
    props.widget.is_active === "true"
  );
});

// Inject all needed data from parent component
const formasi = inject("formasi", {});
const news = inject("news", {});
const slider = inject("slider", {});
const agenda = inject("agenda", []);
const grafik = inject("grafik", []);
const galery = inject("galery", []);
const profil = inject("profil", []);
const linkmedsos = inject("linkmedsos", []);

// Access global Inertia page props (e.g., apps)
const page = usePage();
const apps = computed(() => (page?.props?.value && page.props.value.apps) ? page.props.value.apps : {});

// Log data yang diinject untuk debugging
console.log("Injected data:");
console.log("- formasi:", formasi);
console.log("- news:", news);
console.log("- slider:", slider);
console.log("- agenda:", typeof agenda, agenda);
console.log("- grafik:", grafik);
console.log("- galery:", galery);

// Determine which props to pass based on component type
const componentProps = computed(() => {
  // Start with the widget config as base props
  const baseProps = props.widget.config || {};

  // Ambil nama komponen dari jalur
  const componentPath = props.widget.component || "";
  const componentName = getCleanComponentName(componentPath);

  console.log("Nama komponen untuk props:", componentName);

  // Tentukan props berdasarkan nama komponen
  console.log("Mencoba mencocokkan komponen:", componentName);

  // Log semua komponen yang mungkin untuk debugging
  console.log("Komponen yang tersedia untuk pencocokan:");
  console.log("- Carousel");
  console.log("- BlogSection");
  console.log("- AuthorSection");
  console.log("- AgendaSection");
  console.log("- GraphSection");
  console.log("- CarouselGalery");
  console.log("- SwiperGallery");


  switch (componentName) {
    case "Carousel":
      console.log("Terdeteksi Carousel, passing slider props:", slider);
      return { ...baseProps, slider };
    case "HeroSection":
      console.log("Terdeteksi HeroSection, passing profil props");
      return { ...baseProps, profil };
    case "BlogSection":
      console.log("Terdeteksi BlogSection, passing news props");
      return { ...baseProps, news };
    case "MedsosLink":
      console.log("Terdeteksi MedsosLink, passing galery & apps props");
      return { ...baseProps, linkmedsos };
    case "AuthorSection":
      console.log("Terdeteksi AuthorSection, passing formasi props");
      return { ...baseProps, formasi };
    case "AgendaSection":
      console.log("Terdeteksi AgendaSection, passing agenda props");
      return { ...baseProps, agenda };
    case "GraphSection":
      console.log("Terdeteksi GraphSection, passing grafik props");
      return { ...baseProps, grafik };
    case "JurusanStats":
      console.log("Terdeteksi JurusanStats, passing jurusan props");
      return { ...baseProps, jurusan: props.widget.jurusan || [] };
    case "LayananStats":
      console.log("Terdeteksi LayananStats, passing layanan props");
      return { ...baseProps, layanan: props.widget.layanan || [] };
    case "CarouselGalery":
      console.log("Terdeteksi CarouselGalery, passing galery props");
      return { ...baseProps, galery };
    case "SwiperGallery":
      console.log("Terdeteksi SwiperGallery, passing galery props");
      return { ...baseProps, galery };
    default:
      console.log(
        "Tidak ada jenis komponen yang cocok, menggunakan props default untuk:",
        componentName
      );
      return baseProps;
  }
});

onMounted(() => {
  // Ekstrak jalur komponen dari widget
  const componentPath = props.widget.component;
  console.log("Data widget:", props.widget);
  console.log("Jalur komponen dari database:", componentPath);

  if (componentPath) {
    // Coba impor komponen secara dinamis
    try {
      // Gunakan fungsi yang sama untuk mendapatkan nama komponen konsisten
      const rawComponentName = getCleanComponentName(componentPath);
      console.log("Nama komponen tanpa ekstensi:", rawComponentName);

      // Gunakan pendekatan yang lebih sederhana dengan defineAsyncComponent
      resolvedComponent.value = defineAsyncComponent({
        // Set options
        delay: 200,
        // Increase timeout to handle heavier widgets (e.g., Swiper)
        timeout: 20000,

        // Loader function
        loader: () => {
          console.log(`Memuat komponen: ${rawComponentName}`);

          // Create a mapping of component names to their import paths
          const componentMapping = {
            Carousel: () => import("../Carousel.vue"),
            HeroSection: () => import("../HeroSection.vue"),
            AuthorSection: () => import("../AuthorSection.vue"),
            AgendaSection: () => import("../AgendaSection.vue"),
            BlogSection: () => import("../BlogSection.vue"),
            GraphSection: () => import("../GraphSection.vue"),
            VisitorStats: () => import("../VisitorStats.vue"),
            JurusanStats: () => import("../JurusanStats.vue"),
            LayananStats: () => import("../LayananStats.vue"),
            CarouselGalery: () => import("../CarouselGalery.vue"),
            SwiperGallery: () => import("../SwiperGallery.vue"),
            MedsosLink: () => import("../MedsosLink.vue"),
          };

          // Check if we have a direct mapping for this component
          if (componentMapping[rawComponentName]) {
            console.log(
              `Menggunakan import eksplisit untuk ${rawComponentName}`
            );
            return componentMapping[rawComponentName]();
          }

          // Fallback to dynamic import using relative path
          console.log(`Menggunakan import dinamis untuk ${rawComponentName}`);
          return import(`../${rawComponentName}.vue`).catch((error) => {
            console.error(
              `Gagal memuat dengan jalur ../${rawComponentName}.vue, mencoba jalur alternatif`,
              error
            );

            // Try alternative path (components might be in different locations)
            return import(`@/Components/Web/${rawComponentName}.vue`).catch(
              (error2) => {
                console.error(
                  `Gagal memuat dengan jalur @/Components/Web/${rawComponentName}.vue`,
                  error2
                );
                throw error2;
              }
            );
          });
        },

        // Error handling
        onError(error) {
          console.error(
            `Failed to load widget component: ${componentPath}`,
            error
          );
        },
      });
    } catch (error) {
      console.error(
        `Error setting up async component: ${componentPath}`,
        error
      );
    }
  } else {
    console.error("No component path provided for widget:", props.widget);
  }
});
</script>
